/** @format */
import { useTranslation } from 'next-i18next';
import React, { useEffect, useRef, useState } from 'react';

import { useKeyPress } from '@toucan/client/shared/hooks/use-key-press';

import { SearchFilterMenu } from '@toucan/client/features/search/components/search-filter-menu';
import { CustomSelect } from '@toucan/client/features/search/components/search-filter/components/custom-select';
import { FilterOption } from '@toucan/client/features/search/config/types';

import { SearchFilterProps, SearchFilterSelectOptions } from './types';

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
export const DEFAULT_TEST_ID = 'search-filter';

/**
 * Fixes an issue in storybook where it would constantly fire the useEffect hook
 * watching initialSelectedItems as it was a new array each time
 */
const defaultInitialSelectedItems = [] as FilterOption[];

export const SearchFilter = ({
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    initialSelectedItems = defaultInitialSelectedItems,
    initialIsOpen = false,
    filter,
    id,
    onUpdate,
    onOpen,
    ...otherProps
}: SearchFilterProps) => {
    const { t } = useTranslation();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLElement>(null);
    const [isOpen, setIsOpen] = useState<boolean>(initialIsOpen);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [maybeSelectedItems, setMaybeSelectedItems] =
        useState<FilterOption[]>(initialSelectedItems);
    const [maybeSelectedVariant, setMaybeSelectedVariant] = useState(
        filter.selectedVariant
    );

    // re-initialise the maybe selected items if our initial selected items change
    useEffect(() => {
        setMaybeSelectedItems(initialSelectedItems);
    }, [initialSelectedItems]);

    async function handleToggleOpen() {
        if (!isOpen && onOpen) {
            setIsLoading(true);
            setIsOpen(!isOpen);
            await onOpen(id);
            setIsLoading(false);
        } else {
            setIsOpen(!isOpen);
        }
    }

    function handleCancel() {
        setMaybeSelectedItems(filter.selectedOptions);
        setIsOpen(false);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions, @typescript-eslint/prefer-optional-chain -- Legacy code: do not use as an example.
        buttonRef.current && buttonRef.current.focus();
    }

    function handleApply() {
        onUpdate?.(maybeSelectedItems, maybeSelectedVariant);
        setIsOpen(false);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions, @typescript-eslint/prefer-optional-chain -- Legacy code: do not use as an example.
        buttonRef.current && buttonRef.current.focus();
    }

    function handleClear() {
        setMaybeSelectedItems([]);
    }

    /**
     * Find whether a given filter exists in an array of filters
     *
     * @param {FilterOption} option The filter that we want to find
     * @param {FilterOption[]} selectedOptions The array of filters we want to find the filter in.
     * @returns {boolean} Whether or not the filter exists in the array
     */
    function isSelected(
        option: FilterOption,
        selectedOptions: FilterOption[]
    ): boolean {
        return selectedOptions.some((selectedOption) => {
            return selectedOption.value === option.value;
        });
    }

    function unselectItem(item: FilterOption): void {
        const nextSelectedItems = maybeSelectedItems.filter((selectedItem) => {
            return selectedItem.value !== item.value;
        });
        setMaybeSelectedItems(nextSelectedItems);
    }

    function selectItem(
        item: FilterOption,
        { overwrite }: SearchFilterSelectOptions = {}
    ): void {
        setMaybeSelectedItems((maybeSelectedItems) => {
            return overwrite ? [item] : [...maybeSelectedItems, item];
        });
    }

    function handleSelect(
        selectedItem: FilterOption,
        options?: SearchFilterSelectOptions
    ): void {
        if (
            isSelected(selectedItem, maybeSelectedItems) &&
            !options?.overwrite
        ) {
            unselectItem(selectedItem);
        } else {
            selectItem(selectedItem, options);
        }
    }

    useKeyPress(menuRef.current, ['Escape', 'Esc'], (event: KeyboardEvent) => {
        event.preventDefault();
        handleCancel();
    });

    return (
        <CustomSelect
            id={id}
            isOpen={isOpen}
            isActive={!!filter.selectedOptions.length}
            label={filter?.label}
            closeMenuAriaLabel={t('close menu')}
            openMenuAriaLabel={t('open menu')}
            onClickOut={handleCancel}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises -- Legacy code: do not use as an example.
            onToggleOpen={handleToggleOpen}
            ref={buttonRef}
            data-testid={dataTestId}
            {...otherProps}
        >
            <SearchFilterMenu
                filter={filter}
                selectedOptions={maybeSelectedItems}
                applyLabel={t('Apply')}
                cancelLabel={t('Cancel')}
                clearLabel={t('Clear filter')}
                onSelect={handleSelect}
                onCancel={handleCancel}
                onApply={handleApply}
                onClear={handleClear}
                onSelectVariant={setMaybeSelectedVariant}
                selectedVariant={maybeSelectedVariant}
                ref={menuRef}
                isLoading={isLoading}
                displayOptionQuantity={filter.displayOptionQuantity}
            />
        </CustomSelect>
    );
};
